
import { useState, useRef, useEffect, useCallback } from "react";
import {
    IoChevronBack,
    IoAdd,
    IoTrashOutline,
    IoImageOutline,
    IoCloseCircle,
    IoSend,
    IoStop,
    IoChevronDown,
    IoChevronForward,
} from "react-icons/io5";
import { useNavigationStore } from "../../store/navigationStore";
import MarkdownRenderer from "../markdown/MarkdownRenderer";

const MIN_WIDTH = 480;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 540;
const CHAT_FUNCTION_URL = import.meta.env.VITE_CHAT_FUNCTION_URL;

/* ── Extract final AI text from a stream chunk ── */
const extractTextFromOutput = (output) => {
    let chunk = output;
    if (Array.isArray(output) && output.length === 2 && Array.isArray(output[0])) {
        // Non-empty namespace means this is a subagent message — only show
        // the top-level agent's final response to avoid the content "flipping"
        // between the subagent's intermediate answer and the main agent's reply.
        if (output[0].length > 0) return "";
        chunk = output[1];
    }
    if (!chunk || typeof chunk !== "object") return "";

    for (const nodeOutput of Object.values(chunk)) {
        const messages = nodeOutput?.messages;
        if (!messages?.length) continue;
        for (const msg of messages) {
            if (msg?.kwargs?.tool_calls?.length) continue;
            const id = msg?.id;
            const isAI = Array.isArray(id)
                ? id.some((s) => typeof s === "string" && s.includes("AIMessage"))
                : typeof id === "string" && id.includes("AIMessage");
            if (!isAI) continue;
            const content = msg?.kwargs?.content;
            if (!content) continue;
            if (typeof content === "string" && content.trim()) return content;
            if (Array.isArray(content)) {
                const text = content
                    .filter((b) => b.type === "text" && b.text?.trim())
                    .map((b) => b.text)
                    .join("");
                if (text) return text;
            }
        }
    }
    return "";
};

/* ── Extract activity events + todos from a stream chunk ── */
const extractActivityFromOutput = (output) => {
    let chunk = output;
    let namespace = [];
    if (Array.isArray(output) && output.length === 2 && Array.isArray(output[0])) {
        namespace = output[0];
        chunk = output[1];
    }
    if (!chunk || typeof chunk !== "object") return null;

    const events = [];
    let todos = null;

    for (const [nodeName, nodeOutput] of Object.entries(chunk)) {
        if (nodeOutput?.todos) {
            todos = nodeOutput.todos;
        }

        const messages = nodeOutput?.messages;
        if (!messages?.length) continue;

        for (const msg of messages) {
            const id = msg?.id;
            const kwargs = msg?.kwargs;
            const agentName = kwargs?.name || null;

            if (kwargs?.tool_calls?.length) {
                for (const tc of kwargs.tool_calls) {
                    events.push({
                        type: "tool_call",
                        node: nodeName,
                        toolName: tc.name,
                        args: tc.args,
                        agentName,
                    });
                }
                continue;
            }

            const isToolMsg = Array.isArray(id)
                ? id.some((s) => typeof s === "string" && s.includes("ToolMessage"))
                : typeof id === "string" && id?.includes("ToolMessage");
            if (isToolMsg) {
                events.push({ type: "tool_result", node: nodeName, toolName: kwargs?.name });
                continue;
            }

            const isAI = Array.isArray(id)
                ? id.some((s) => typeof s === "string" && s.includes("AIMessage"))
                : typeof id === "string" && id?.includes("AIMessage");
            if (isAI) {
                const content = kwargs?.content;
                const hasText =
                    typeof content === "string"
                        ? !!content.trim()
                        : Array.isArray(content)
                            ? content.some((b) => b.type === "text" && b.text?.trim())
                            : false;
                events.push({ type: "ai_message", node: nodeName, hasContent: hasText, agentName });
                continue;
            }

            const isHuman = Array.isArray(id)
                ? id.some((s) => typeof s === "string" && s.includes("HumanMessage"))
                : typeof id === "string" && id?.includes("HumanMessage");
            if (isHuman) {
                events.push({ type: "model_request", node: nodeName, agentName });
            }
        }
    }

    const hasData = events.length > 0 || todos;
    return hasData ? { namespace, events, todos } : null;
};

/* ── Helpers ── */
const truncate = (str, max) => (str?.length > max ? str.slice(0, max) + "\u2026" : str || "");

const formatToolDetail = (toolName, args) => {
    if (!args || typeof args !== "object") return null;
    if (toolName === "guide_search") {
        if (args.queries?.length) return `queries: ${args.queries.map((q) => `"${truncate(q, 40)}"`).join(", ")}`;
        if (args.query) return `query: "${truncate(args.query, 90)}"`;
        return null;
    }
    if (toolName === "read_file") return `path: ${args.file_path || args.path || ""}`;
    if (toolName === "task") {
        const desc = args.description ? `: "${truncate(args.description, 70)}"` : "";
        return `\u2192 ${args.subagent_type || "subagent"}${desc}`;
    }
    if (toolName === "advisor") {
        return args.question ? `"${truncate(args.question, 80)}"` : "consulting advisor\u2026";
    }
    if (toolName === "write_todos") {
        const count = args.todos?.length;
        return count ? `${count} task${count !== 1 ? "s" : ""}` : null;
    }
    const entries = Object.entries(args).filter(([, v]) => v != null);
    if (!entries.length) return null;
    return entries
        .slice(0, 3)
        .map(([k, v]) => `${k}: ${truncate(String(v), 50)}`)
        .join("  \u00b7  ");
};

/* ── Infer tool name from parsed args (Bedrock omits name in streaming chunks) ── */
const inferToolName = (args) => {
    if (!args || typeof args !== "object") return null;
    if (args.subagent_type != null || (args.description != null && args.prompt != null)) return "task";
    if (args.file_path || (args.path && args.limit != null)) return "read_file";
    if (args.query || args.queries) return "guide_search";
    if (args.todos) return "write_todos";
    if (args.situation != null && args.question != null) return "advisor";
    if (args.content != null && args.path) return "write_file";
    return null;
};

/* ── Shared keyframes ── */
const SharedStyles = () => (
    <style>{`
        @keyframes chatDotPulse { 0%,80%,100%{opacity:.3;transform:scale(.8)} 40%{opacity:1;transform:scale(1)} }
        @keyframes chatStepSpin { to{transform:rotate(360deg)} }
        @keyframes chatActivityPulse { 0%,100%{opacity:.6} 50%{opacity:1} }
        @keyframes chatProgressStripe {
            0% { background-position: 1rem 0 }
            100% { background-position: 0 0 }
        }
    `}</style>
);

const ThinkingDots = () => (
    <span style={{ display: "inline-flex", gap: "4px", alignItems: "center", height: "1.35em" }}>
        {[0, 1, 2].map((i) => (
            <span
                key={i}
                style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor: "var(--grey)",
                    display: "inline-block",
                    animation: `chatDotPulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                }}
            />
        ))}
    </span>
);

/* ── Status icon (shared by steps + todos) ── */
const StatusIcon = ({ status, size = 14 }) => {
    if (status === "done" || status === "completed") {
        return (
            <svg width={size} height={size} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
                <circle cx="7" cy="7" r="6" fill="var(--green, #22c55e)" />
                <path
                    d="M4 7l2 2 4-4"
                    stroke="#fff"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    }
    if (status === "running" || status === "in_progress") {
        return (
            <span
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: "50%",
                    border: "2px solid var(--skyDark)",
                    borderTopColor: "transparent",
                    display: "inline-block",
                    flexShrink: 0,
                    animation: "chatStepSpin 0.8s linear infinite",
                }}
            />
        );
    }
    if (status === "error") {
        return (
            <svg width={size} height={size} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
                <circle cx="7" cy="7" r="6" fill="var(--red, #ef4444)" />
                <path
                    d="M5 5l4 4M9 5l-4 4"
                    stroke="#fff"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                />
            </svg>
        );
    }
    return (
        <span
            style={{
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: "50%",
                border: "2px solid var(--lighterGrey)",
                display: "inline-block",
                flexShrink: 0,
            }}
        />
    );
};

const STEP_BADGE = {
    tool_call: { bg: "rgba(14,165,233,0.10)", color: "var(--skyDark)", label: "TOOL" },
    model_request: { bg: "rgba(245,158,11,0.10)", color: "var(--orange, #f59e0b)", label: "LLM" },
};

/* ── Real-time activity indicator ── */
const ActivityIndicator = ({ activity }) => {
    if (!activity) return null;
    const typeColor = {
        tool_call: "var(--skyDark)",
        tool_result: "var(--green, #22c55e)",
        model_request: "var(--orange, #f59e0b)",
        ai_message: "var(--skyDark)",
        thinking: "var(--grey)",
        connecting: "var(--grey)",
    };
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: ".35rem",
                padding: ".35rem .6rem",
                marginTop: ".4rem",
                borderRadius: ".35rem",
                backgroundColor: "rgba(0,0,0,0.03)",
                fontSize: ".78rem",
                color: "var(--darkGrey)",
                animation: "chatActivityPulse 2s ease-in-out infinite",
            }}
        >
            <span
                style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor: typeColor[activity.type] || "var(--grey)",
                    flexShrink: 0,
                }}
            />
            <div style={{ minWidth: 0 }}>
                <span style={{ fontWeight: 500 }}>{activity.label}</span>
                {activity.detail && (
                    <span
                        style={{
                            display: "block",
                            fontSize: ".72rem",
                            color: "var(--grey)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            marginTop: ".1rem",
                        }}
                    >
                        {activity.detail}
                    </span>
                )}
            </div>
        </div>
    );
};

/* ── Todos panel (Cursor-style task list) ── */
const TodosPanel = ({ todos, isStreaming }) => {
    const [collapsed, setCollapsed] = useState(false);

    if (!todos?.length) return null;

    const completed = todos.filter((t) => t.status === "completed").length;
    const total = todos.length;
    const progress = total > 0 ? (completed / total) * 100 : 0;
    const allDone = completed === total;

    return (
        <div
            style={{
                marginTop: ".5rem",
                border: "1px solid var(--lighterGrey)",
                borderRadius: ".5rem",
                overflow: "hidden",
                fontSize: ".82rem",
            }}
        >
            {/* Header */}
            <div
                onClick={() => setCollapsed((c) => !c)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: ".4rem .6rem",
                    backgroundColor: "rgba(0,0,0,0.02)",
                    borderBottom: collapsed ? "none" : "1px solid var(--lighterGrey)",
                    cursor: "pointer",
                    userSelect: "none",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: ".3rem" }}>
                    {collapsed ? <IoChevronForward size={11} /> : <IoChevronDown size={11} />}
                    <span style={{ fontWeight: 600, fontSize: ".75rem", color: "var(--darkGrey)" }}>
                        Agent Tasks
                    </span>
                </div>
                <span
                    style={{
                        fontSize: ".68rem",
                        fontWeight: 600,
                        color: allDone ? "var(--green, #22c55e)" : "var(--grey)",
                    }}
                >
                    {completed}/{total}
                </span>
            </div>

            {!collapsed && (
                <>
                    {/* Progress bar */}
                    <div style={{ height: "3px", backgroundColor: "var(--lighterGrey)" }}>
                        <div
                            style={{
                                height: "100%",
                                width: `${progress}%`,
                                backgroundColor: allDone
                                    ? "var(--green, #22c55e)"
                                    : "var(--skyDark)",
                                transition: "width 300ms ease",
                                ...(isStreaming && !allDone
                                    ? {
                                        backgroundImage:
                                            "linear-gradient(45deg, rgba(255,255,255,.2) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.2) 50%, rgba(255,255,255,.2) 75%, transparent 75%)",
                                        backgroundSize: "1rem 1rem",
                                        animation: "chatProgressStripe .6s linear infinite",
                                    }
                                    : {}),
                            }}
                        />
                    </div>

                    {/* Items */}
                    <div style={{ padding: ".3rem .4rem" }}>
                        {todos.map((todo, i) => (
                            <div
                                key={i}
                                style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: ".4rem",
                                    padding: ".25rem .25rem",
                                }}
                            >
                                <div style={{ paddingTop: "1px" }}>
                                    <StatusIcon status={todo.status} size={14} />
                                </div>
                                <span
                                    style={{
                                        color:
                                            todo.status === "completed"
                                                ? "var(--grey)"
                                                : "var(--darkGrey)",
                                        textDecoration:
                                            todo.status === "completed" ? "line-through" : "none",
                                        fontWeight: todo.status === "in_progress" ? 500 : 400,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {todo.content}
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

/* ── Hierarchical steps accordion (Agent → tool calls) ── */
const AgentStepsAccordion = ({ steps, todos, isStreaming }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [collapsedAgents, setCollapsedAgents] = useState(() => new Set());
    const wasStreaming = useRef(isStreaming);

    useEffect(() => {
        if (wasStreaming.current && !isStreaming) setCollapsed(true);
        wasStreaming.current = isStreaming;
    }, [isStreaming]);

    if (!steps?.length) return null;

    const agentOrder = [];
    const agentGroupMap = new Map();
    for (const step of steps) {
        const agent = step.agent || "Main Agent";
        if (!agentGroupMap.has(agent)) {
            agentGroupMap.set(agent, []);
            agentOrder.push(agent);
        }
        agentGroupMap.get(agent).push(step);
    }

    const totalDone = steps.filter((s) => s.status === "done").length;
    const totalErrors = steps.filter((s) => s.status === "error").length;
    const elapsed =
        steps.length > 1
            ? ((steps[steps.length - 1].timestamp - steps[0].timestamp) / 1000).toFixed(1)
            : null;

    const toggleAgent = (agent) =>
        setCollapsedAgents((prev) => {
            const next = new Set(prev);
            if (next.has(agent)) next.delete(agent);
            else next.add(agent);
            return next;
        });

    const MAIN_COLOR = { bg: "rgba(99,102,241,0.07)", border: "rgba(99,102,241,0.18)", text: "#6366f1" };
    const SUB_COLOR = { bg: "rgba(14,165,233,0.07)", border: "rgba(14,165,233,0.18)", text: "var(--skyDark)" };

    return (
        <div style={{ marginTop: ".4rem", fontSize: ".78rem" }}>
            {/* Top-level header */}
            <div
                onClick={() => setCollapsed((c) => !c)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: ".3rem",
                    cursor: "pointer",
                    color: totalErrors ? "var(--red, #ef4444)" : "var(--grey)",
                    padding: ".15rem 0",
                    userSelect: "none",
                }}
            >
                {collapsed ? <IoChevronForward size={11} /> : <IoChevronDown size={11} />}
                <span>
                    {totalDone}/{steps.length} steps
                    {todos?.length > 0 && ` \u00b7 ${todos.length} tasks`}
                    {totalErrors > 0 && ` \u00b7 ${totalErrors} failed`}
                    {elapsed ? ` \u00b7 ${elapsed}s` : ""}
                </span>
            </div>

            {!collapsed && (
                <div style={{ display: "flex", flexDirection: "column", gap: ".3rem", marginTop: ".25rem" }}>
                    {todos?.length > 0 && (
                        <TodosPanel todos={todos} isStreaming={isStreaming} />
                    )}
                    {agentOrder.map((agent) => {
                        const agentSteps = agentGroupMap.get(agent);
                        const agentDone = agentSteps.filter((s) => s.status === "done").length;
                        const agentRunning = agentSteps.some((s) => s.status === "running");
                        const agentError = agentSteps.some((s) => s.status === "error");
                        const isAgentCollapsed = collapsedAgents.has(agent);
                        const colors = agent === "Main Agent" ? MAIN_COLOR : SUB_COLOR;

                        return (
                            <div
                                key={agent}
                                style={{
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: ".4rem",
                                    overflow: "hidden",
                                }}
                            >
                                {/* Agent header */}
                                <div
                                    onClick={() => toggleAgent(agent)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: ".3rem",
                                        padding: ".35rem .5rem",
                                        cursor: "pointer",
                                        userSelect: "none",
                                        backgroundColor: colors.bg,
                                    }}
                                >
                                    <StatusIcon
                                        status={agentError ? "error" : agentRunning ? "running" : "done"}
                                        size={12}
                                    />
                                    {isAgentCollapsed ? (
                                        <IoChevronForward size={10} />
                                    ) : (
                                        <IoChevronDown size={10} />
                                    )}
                                    <span
                                        style={{
                                            fontWeight: 600,
                                            color: colors.text,
                                            fontSize: ".76rem",
                                        }}
                                    >
                                        {agent}
                                    </span>
                                    <span
                                        style={{
                                            marginLeft: "auto",
                                            fontSize: ".68rem",
                                            color: "var(--grey)",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {agentDone}/{agentSteps.length}
                                    </span>
                                </div>

                                {/* Steps within agent */}
                                {!isAgentCollapsed && (
                                    <div style={{ padding: ".15rem .35rem .25rem" }}>
                                        {agentSteps.map((step) => {
                                            const badge = STEP_BADGE[step.type];
                                            return (
                                                <div key={step.id} style={{ padding: ".12rem .2rem" }}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: ".3rem",
                                                        }}
                                                    >
                                                        <StatusIcon status={step.status} size={12} />
                                                        {badge && (
                                                            <span
                                                                style={{
                                                                    fontSize: ".58rem",
                                                                    fontWeight: 700,
                                                                    padding: ".05rem .25rem",
                                                                    borderRadius: ".15rem",
                                                                    backgroundColor: badge.bg,
                                                                    color: badge.color,
                                                                    letterSpacing: ".03em",
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {badge.label}
                                                            </span>
                                                        )}
                                                        <span
                                                            style={{
                                                                color:
                                                                    step.status === "done"
                                                                        ? "var(--grey)"
                                                                        : step.status === "error"
                                                                            ? "var(--red, #ef4444)"
                                                                            : "var(--darkGrey)",
                                                                fontWeight:
                                                                    step.status === "running" ? 500 : 400,
                                                            }}
                                                        >
                                                            {step.toolName || step.label}
                                                        </span>
                                                    </div>
                                                    {step.detail && (
                                                        <div
                                                            style={{
                                                                paddingLeft: "calc(12px + .3rem + .25rem)",
                                                                fontSize: ".72rem",
                                                                color: "var(--grey)",
                                                                lineHeight: 1.3,
                                                                marginTop: ".1rem",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {step.detail}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ════════════════════════════════════════════════════════════ */

const ChatSidebar = () => {
    const { isChatSidebarOpen, setIsChatSidebarOpen } = useNavigationStore();
    const [threads, setThreads] = useState([]);
    const [activeThreadId, setActiveThreadId] = useState(null);
    const [showThreadList, setShowThreadList] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const mode = "agent";
    const [attachments, setAttachments] = useState([]);
    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const [isDragging, setIsDragging] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamActivity, setStreamActivity] = useState(null);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const isNearBottomRef = useRef(true);
    const fileInputRef = useRef(null);
    const dragStartX = useRef(0);
    const dragStartWidth = useRef(0);
    const abortControllerRef = useRef(null);

    const activeThread = threads.find((t) => t.id === activeThreadId);

    const handleMessagesScroll = useCallback(() => {
        const el = messagesContainerRef.current;
        if (!el) return;
        const threshold = 80;
        isNearBottomRef.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    }, []);

    useEffect(() => {
        if (isNearBottomRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [
        activeThread?.messages?.length,
        activeThread?.messages?.at(-1)?.content,
        activeThread?.messages?.at(-1)?.steps?.length,
        activeThread?.messages?.at(-1)?.todos,
    ]);

    // --- Resize drag ---
    const handleMouseDown = useCallback(
        (e) => {
            e.preventDefault();
            setIsDragging(true);
            dragStartX.current = e.clientX;
            dragStartWidth.current = width;
        },
        [width]
    );

    useEffect(() => {
        if (!isDragging) return;
        const handleMouseMove = (e) => {
            const delta = dragStartX.current - e.clientX;
            const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth.current + delta));
            setWidth(newWidth);
        };
        const handleMouseUp = () => setIsDragging(false);
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [isDragging]);

    // --- Send message & stream response ---
    const handleSend = async () => {
        const question = inputValue.trim();
        if (!question || isStreaming) return;

        isNearBottomRef.current = true;
        setInputValue("");
        setAttachments([]);

        let threadId = activeThreadId;

        if (!threadId || !threads.find((t) => t.id === threadId)) {
            threadId = crypto.randomUUID();
            const newThread = {
                id: threadId,
                title: question.length > 45 ? question.slice(0, 45) + "\u2026" : question,
                createdAt: new Date().toISOString(),
                messages: [],
            };
            setThreads((prev) => [newThread, ...prev]);
            setActiveThreadId(threadId);
        }

        const userMsg = {
            id: `m${Date.now()}`,
            role: "user",
            content: question,
            mode,
            images: attachments.map((a) => a.preview),
        };

        const assistantMsgId = `m${Date.now() + 1}`;
        const assistantMsg = {
            id: assistantMsgId,
            role: "assistant",
            content: "",
            streaming: true,
            steps: [],
            todos: [],
        };

        setThreads((prev) =>
            prev.map((t) =>
                t.id === threadId ? { ...t, messages: [...t.messages, userMsg, assistantMsg] } : t
            )
        );

        setIsStreaming(true);
        setStreamActivity({ label: "Connecting\u2026", type: "connecting" });
        abortControllerRef.current = new AbortController();

        const updateMsg = (updater) => {
            setThreads((prev) =>
                prev.map((t) => {
                    if (t.id !== threadId) return t;
                    return {
                        ...t,
                        messages: t.messages.map((m) =>
                            m.id === assistantMsgId
                                ? typeof updater === "function"
                                    ? updater(m)
                                    : { ...m, ...updater }
                                : m
                        ),
                    };
                })
            );
        };

        const addStep = (step) => {
            updateMsg((m) => ({
                ...m,
                steps: [
                    ...(m.steps || []).map((s) =>
                        s.status === "running" && s.type !== "tool_call" ? { ...s, status: "done" } : s
                    ),
                    step,
                ],
            }));
        };

        const markToolsDone = () => {
            updateMsg((m) => ({
                ...m,
                steps: (m.steps || []).map((s) =>
                    s.type === "tool_call" && s.status === "running" ? { ...s, status: "done" } : s
                ),
            }));
        };

        try {
            const response = await fetch(CHAT_FUNCTION_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json", "x-api-key": "shhh" },
                body: JSON.stringify({ message: question, threadId }),
                signal: abortControllerRef.current.signal,
            });

            if (!response.ok) throw new Error(`Request failed (${response.status})`);
            if (!response.body) throw new Error("No response body");

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let serverThreadId = threadId;
            let accumulatedText = "";
            let hasTokenStream = false;
            const seenToolCallKeys = new Set();
            const seenLgSteps = new Set();
            const toolCallTracking = new Map();
            const toolArgsAccum = new Map();
            const nsIdToAgent = new Map();
            let lastNsDepth = 0;

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop() || "";

                for (const part of parts) {
                    if (!part.startsWith("data: ")) continue;
                    try {
                        const parsed = JSON.parse(part.substring(6).trim());
                        if (parsed.threadId) serverThreadId = parsed.threadId;

                        const rawOutput = parsed.output;

                        // ── Detect stream format ──
                        // deepagents:  [namespace[], "messages"|"updates", [chunk, metadata]]
                        // LangGraph multi-mode: ["messages"|"updates", nested_data]
                        // LangGraph single-mode: {node: {messages: [...]}}
                        let sseMode = null;
                        let payload = rawOutput;
                        let chunkNs = [];

                        if (
                            Array.isArray(rawOutput) &&
                            rawOutput.length === 3 &&
                            Array.isArray(rawOutput[0]) &&
                            typeof rawOutput[1] === "string"
                        ) {
                            chunkNs = rawOutput[0];
                            sseMode = rawOutput[1];
                            payload = rawOutput[2];
                        } else if (
                            Array.isArray(rawOutput) &&
                            rawOutput.length === 2 &&
                            typeof rawOutput[0] === "string" &&
                            (rawOutput[0] === "messages" || rawOutput[0] === "updates")
                        ) {
                            sseMode = rawOutput[0];
                            payload = rawOutput[1];
                        }

                        if (sseMode === "messages") {
                            // deepagents: payload = [chunk, metadata]
                            // standard LangGraph: payload = [namespace[], [chunk, metadata]]
                            let msgChunk, metadata;

                            if (chunkNs.length > 0) {
                                msgChunk = Array.isArray(payload) ? payload[0] : payload;
                                metadata = Array.isArray(payload) ? payload[1] : {};
                            } else {
                                let ns = [];
                                let msgData = payload;
                                if (Array.isArray(payload) && Array.isArray(payload[0])) {
                                    ns = payload[0];
                                    msgData = payload.slice(1);
                                }
                                if (ns.length > 0) continue;
                                const raw = Array.isArray(msgData[0]) ? msgData[0][0] : msgData[0];
                                msgChunk = raw;
                                metadata = Array.isArray(msgData[0]) ? msgData[0][1] : {};
                            }

                            if (!msgChunk) continue;

                            const id = msgChunk?.id;
                            const kwargs = msgChunk?.kwargs;
                            const runId = kwargs?.id;
                            const lgStep = metadata?.langgraph_step;
                            const lgNode = metadata?.langgraph_node || "";
                            const nsDepth = chunkNs.length;
                            const nsParentId = nsDepth >= 2 ? chunkNs[0] : null;

                            // Derive agent label from namespace depth
                            let agentPrefix = "";
                            if (nsParentId) {
                                if (!nsIdToAgent.has(nsParentId)) {
                                    // Collect ALL subagent names from task
                                    // tool calls in insertion order
                                    const taskNames = [];
                                    const argSources = [
                                        ...Array.from(toolCallTracking.values()).map((i) => i.args),
                                        ...toolArgsAccum.values(),
                                    ];
                                    for (const raw of argSources) {
                                        if (!raw) continue;
                                        try {
                                            const parsed = JSON.parse(raw);
                                            const val =
                                                parsed.subagent_type ||
                                                parsed.subagent ||
                                                parsed.agent;
                                            if (val) taskNames.push(val);
                                        } catch {
                                            const m = raw.match(
                                                /(?:"subagent(?:_type)?"|"agent")\s*:\s*"([^"]+)"/
                                            );
                                            if (m) taskNames.push(m[1]);
                                        }
                                    }

                                    // Assign the next unmatched name by index
                                    const idx = nsIdToAgent.size;
                                    let name = taskNames[idx] || taskNames[0] || "subagent";

                                    // Disambiguate when same type appears
                                    // multiple times
                                    const existing = Array.from(nsIdToAgent.values());
                                    const sameCount = existing.filter(
                                        (v) => v === name || v.startsWith(name + " (")
                                    ).length;
                                    if (sameCount > 0) {
                                        name = `${name} (${sameCount + 1})`;
                                    }

                                    nsIdToAgent.set(nsParentId, name);
                                }
                                agentPrefix = nsIdToAgent.get(nsParentId);
                            }

                            const withPrefix = (label) =>
                                agentPrefix ? `${agentPrefix} \u203a ${label}` : label;

                            const isAIChunk = Array.isArray(id)
                                ? id.some(
                                    (s) =>
                                        typeof s === "string" &&
                                        (s.includes("AIMessageChunk") || s.includes("AIMessage"))
                                )
                                : typeof id === "string" &&
                                    (id.includes("AIMessageChunk") || id.includes("AIMessage"));

                            const isToolMsg = Array.isArray(id)
                                ? id.some((s) => typeof s === "string" && s.includes("ToolMessage"))
                                : typeof id === "string" && id?.includes("ToolMessage");

                            const agentLabel = agentPrefix || "Main Agent";

                            // Track model request rounds as steps
                            if (isAIChunk && lgStep != null) {
                                const stepKey = `${nsDepth}:${lgNode}:${lgStep}`;
                                if (!seenLgSteps.has(stepKey)) {
                                    seenLgSteps.add(stepKey);
                                    addStep({
                                        id: `s-mr-${nsDepth}-${lgStep}`,
                                        type: "model_request",
                                        agent: agentLabel,
                                        label: "Thinking\u2026",
                                        detail: null,
                                        status: "running",
                                        timestamp: Date.now(),
                                    });
                                    setStreamActivity({
                                        label: withPrefix("Sending to model\u2026"),
                                        type: "model_request",
                                    });
                                }
                            }

                            if (isAIChunk) {
                                const hasToolChunks =
                                    kwargs?.tool_call_chunks?.length > 0;

                                if (hasToolChunks) {
                                    for (const tc of kwargs.tool_call_chunks) {
                                        const idx = tc.index ?? 0;
                                        const key = `${runId}:${idx}`;
                                        const stepId = `s-tc-${runId}-${idx}`;

                                        if (!toolCallTracking.has(key)) {
                                            toolCallTracking.set(key, {
                                                args: "",
                                                agent: agentLabel,
                                                stepId,
                                                name: tc.name || null,
                                            });
                                        }
                                        const info = toolCallTracking.get(key);
                                        if (tc.name && !info.name) info.name = tc.name;
                                        info.args += tc.args || "";

                                        if (runId) {
                                            const prev = toolArgsAccum.get(runId) || "";
                                            toolArgsAccum.set(runId, prev + (tc.args || ""));
                                        }

                                        if (!seenToolCallKeys.has(key)) {
                                            seenToolCallKeys.add(key);
                                            updateMsg((m) => ({
                                                ...m,
                                                steps: (m.steps || []).map((s) =>
                                                    s.type === "model_request" &&
                                                    s.status === "running" &&
                                                    s.agent === agentLabel
                                                        ? { ...s, label: "Planning tool calls" }
                                                        : s
                                                ),
                                            }));
                                            addStep({
                                                id: stepId,
                                                type: "tool_call",
                                                agent: agentLabel,
                                                label: info.name || "Calling tool\u2026",
                                                toolName: info.name || null,
                                                detail: null,
                                                status: "running",
                                                timestamp: Date.now(),
                                            });
                                        }

                                        if (info.args) {
                                            try {
                                                const parsed = JSON.parse(info.args);
                                                const inferred = info.name || inferToolName(parsed);
                                                const detail = formatToolDetail(inferred, parsed);
                                                const stepUpdates = {};
                                                if (inferred && !info.name) {
                                                    info.name = inferred;
                                                    stepUpdates.label = inferred;
                                                    stepUpdates.toolName = inferred;
                                                }
                                                if (detail) stepUpdates.detail = detail;

                                                if (inferred === "write_todos" && parsed.todos?.length) {
                                                    updateMsg((m) => ({
                                                        ...m,
                                                        todos: parsed.todos,
                                                        steps: Object.keys(stepUpdates).length
                                                            ? (m.steps || []).map((s) =>
                                                                s.id === stepId ? { ...s, ...stepUpdates } : s
                                                            )
                                                            : m.steps,
                                                    }));
                                                } else if (Object.keys(stepUpdates).length) {
                                                    updateMsg((m) => ({
                                                        ...m,
                                                        steps: (m.steps || []).map((s) =>
                                                            s.id === stepId ? { ...s, ...stepUpdates } : s
                                                        ),
                                                    }));
                                                }
                                            } catch { /* args still incomplete */ }
                                        }
                                    }
                                    setStreamActivity({
                                        label: withPrefix("Preparing tool call\u2026"),
                                        type: "tool_call",
                                    });
                                } else {
                                    const content = kwargs?.content;
                                    let newText = "";
                                    if (typeof content === "string") {
                                        newText = content;
                                    } else if (Array.isArray(content)) {
                                        for (const block of content) {
                                            if (block.type === "text" && block.text) {
                                                newText += block.text;
                                            }
                                        }
                                    }
                                    if (newText) {
                                        if (nsDepth <= 1) {
                                            hasTokenStream = true;
                                            accumulatedText += newText;
                                            updateMsg((m) => ({
                                                ...m,
                                                content: accumulatedText,
                                                steps: (m.steps || []).map((s) =>
                                                    s.type === "model_request" &&
                                                    s.status === "running" &&
                                                    s.agent === agentLabel
                                                        ? { ...s, label: "Generating response" }
                                                        : s
                                                ),
                                            }));
                                        } else {
                                            updateMsg((m) => ({
                                                ...m,
                                                steps: (m.steps || []).map((s) =>
                                                    s.type === "model_request" &&
                                                    s.status === "running" &&
                                                    s.agent === agentLabel
                                                        ? { ...s, label: "Preparing response for supervisor" }
                                                        : s
                                                ),
                                            }));
                                        }
                                        setStreamActivity({
                                            label: withPrefix("Generating response\u2026"),
                                            type: "ai_message",
                                        });
                                    }
                                }
                            }

                            if (isToolMsg) {
                                const toolName = kwargs?.name || "tool";
                                updateMsg((m) => {
                                    let matched = false;
                                    return {
                                        ...m,
                                        steps: (m.steps || []).map((s) => {
                                            if (
                                                !matched &&
                                                s.type === "tool_call" &&
                                                s.status === "running" &&
                                                s.agent === agentLabel
                                            ) {
                                                matched = true;
                                                return { ...s, status: "done", label: toolName, toolName };
                                            }
                                            return s;
                                        }),
                                    };
                                });
                                setStreamActivity({
                                    label: withPrefix(`Result from ${toolName}`),
                                    type: "tool_result",
                                });
                            }

                            lastNsDepth = nsDepth;
                            continue;
                        }

                        // --- "updates" mode or legacy single-mode format ---
                        const updateData = sseMode === "updates" ? payload : rawOutput;

                        const activity = extractActivityFromOutput(updateData);
                        if (activity) {
                            if (activity.todos) {
                                updateMsg({ todos: activity.todos });
                            }

                            for (const event of activity.events) {
                                const evtAgentLabel =
                                    event.agentName && event.agentName !== "model"
                                        ? event.agentName
                                        : "Main Agent";
                                const evtPrefix =
                                    evtAgentLabel !== "Main Agent"
                                        ? `${evtAgentLabel} \u203a `
                                        : "";

                                if (event.type === "tool_call") {
                                    const detail = formatToolDetail(event.toolName, event.args);
                                    setStreamActivity({
                                        label: `Calling ${evtPrefix}${event.toolName}\u2026`,
                                        type: "tool_call",
                                        detail,
                                    });
                                    addStep({
                                        id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                                        type: "tool_call",
                                        agent: evtAgentLabel,
                                        label: event.toolName,
                                        toolName: event.toolName,
                                        detail,
                                        status: "running",
                                        timestamp: Date.now(),
                                    });
                                } else if (event.type === "tool_result") {
                                    setStreamActivity({
                                        label: `${evtPrefix}Result received`,
                                        type: "tool_result",
                                    });
                                    markToolsDone();
                                } else if (event.type === "model_request") {
                                    setStreamActivity({
                                        label: `${evtPrefix}Sending to model\u2026`,
                                        type: "model_request",
                                    });
                                    addStep({
                                        id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                                        type: "model_request",
                                        agent: evtAgentLabel,
                                        label: "Model request",
                                        detail: null,
                                        status: "running",
                                        timestamp: Date.now(),
                                    });
                                } else if (event.type === "ai_message") {
                                    setStreamActivity({
                                        label: event.hasContent
                                            ? `${evtPrefix}Generating response\u2026`
                                            : `${evtPrefix}Thinking\u2026`,
                                        type: event.hasContent ? "ai_message" : "thinking",
                                    });
                                }
                            }
                        }

                        // Fallback: extract text from updates only when token
                        // streaming isn't active (legacy single-mode backends)
                        if (!hasTokenStream) {
                            const text = extractTextFromOutput(updateData);
                            if (text) {
                                updateMsg({ content: text });
                            }
                        }
                    } catch (e) {
                        console.error("Failed to parse SSE event:", e);
                    }
                }
            }

            setStreamActivity(null);
            updateMsg((m) => {
                const lastMrIdx = new Map();
                (m.steps || []).forEach((s, i) => {
                    if (s.type === "model_request" && s.agent !== "Main Agent") {
                        lastMrIdx.set(s.agent, i);
                    }
                });
                return {
                    ...m,
                    streaming: false,
                    steps: (m.steps || []).map((s, i) => {
                        let step = s;
                        if (step.status === "running") step = { ...step, status: "done" };
                        if (lastMrIdx.get(step.agent) === i) {
                            step = { ...step, label: "Returned results to supervisor" };
                        }
                        return step;
                    }),
                };
            });
        } catch (error) {
            setStreamActivity(null);
            if (error.name === "AbortError") {
                updateMsg((m) => ({
                    ...m,
                    streaming: false,
                    steps: (m.steps || []).map((s) =>
                        s.status === "running" ? { ...s, status: "done" } : s
                    ),
                }));
            } else {
                updateMsg((m) => ({
                    ...m,
                    content: m.content || `Something went wrong: ${error.message}`,
                    streaming: false,
                    isError: !m.content,
                    steps: (m.steps || []).map((s) =>
                        s.status === "running" ? { ...s, status: "error" } : s
                    ),
                }));
            }
        }

        setIsStreaming(false);
        abortControllerRef.current = null;
    };

    const handleStopStreaming = () => {
        abortControllerRef.current?.abort();
        setStreamActivity(null);
    };

    // --- Image attachments ---
    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files || []);
        const imageFiles = files.filter((f) => f.type.startsWith("image/"));
        const newAttachments = imageFiles.map((f) => ({
            id: `img-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            file: f,
            preview: URL.createObjectURL(f),
        }));
        setAttachments((prev) => [...prev, ...newAttachments]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removeAttachment = (id) => {
        setAttachments((prev) => {
            const removed = prev.find((a) => a.id === id);
            if (removed) URL.revokeObjectURL(removed.preview);
            return prev.filter((a) => a.id !== id);
        });
    };

    // --- Thread management ---
    const handleNewThread = () => {
        const newThread = {
            id: `t${Date.now()}`,
            title: "New conversation",
            createdAt: new Date().toISOString(),
            messages: [],
        };
        setThreads((prev) => [newThread, ...prev]);
        setActiveThreadId(newThread.id);
        setShowThreadList(false);
    };

    const handleDeleteThread = (delId) => {
        setThreads((prev) => {
            const next = prev.filter((t) => t.id !== delId);
            if (activeThreadId === delId && next.length) {
                setActiveThreadId(next[0].id);
            } else if (activeThreadId === delId) {
                setActiveThreadId(null);
            }
            return next;
        });
    };

    if (!isChatSidebarOpen) return null;

    return (
        <div
            style={{
                width: `${width}px`,
                minWidth: `${MIN_WIDTH}px`,
                maxWidth: `${MAX_WIDTH}px`,
                height: "calc(100dvh - 3rem)",
                display: "flex",
                flexDirection: "row",
                backgroundColor: "var(--white)",
                position: "relative",
            }}
        >
            <SharedStyles />
            {/* Drag handle */}
            <div
                onMouseDown={handleMouseDown}
                style={{
                    width: "4px",
                    cursor: "col-resize",
                    backgroundColor: isDragging ? "var(--skyDark)" : "transparent",
                    transition: isDragging ? "none" : "background-color 0.15s",
                    flexShrink: 0,
                    borderLeft: "1px solid var(--lighterGrey)",
                }}
                onMouseEnter={(e) => {
                    if (!isDragging) e.currentTarget.style.backgroundColor = "var(--lightGrey)";
                }}
                onMouseLeave={(e) => {
                    if (!isDragging) e.currentTarget.style.backgroundColor = "transparent";
                }}
            />
            <div
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: ".6rem .8rem",
                        borderBottom: "1px solid var(--lightestGrey)",
                        gap: ".5rem",
                        minHeight: "48px",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: ".5rem",
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {showThreadList ? (
                            <div
                                style={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    padding: ".2rem",
                                }}
                                onClick={() => setShowThreadList(false)}
                                title="Back to chat"
                            >
                                <IoChevronBack size="1.2rem" />
                            </div>
                        ) : (
                            <div
                                style={{
                                    cursor: "pointer",
                                    padding: ".3rem",
                                    borderRadius: ".3rem",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                                onClick={() => setShowThreadList(true)}
                                title="View all conversations"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <rect
                                        x="1"
                                        y="2"
                                        width="14"
                                        height="2"
                                        rx="1"
                                        fill="currentColor"
                                    />
                                    <rect
                                        x="1"
                                        y="7"
                                        width="14"
                                        height="2"
                                        rx="1"
                                        fill="currentColor"
                                    />
                                    <rect
                                        x="1"
                                        y="12"
                                        width="14"
                                        height="2"
                                        rx="1"
                                        fill="currentColor"
                                    />
                                </svg>
                            </div>
                        )}
                        <p
                            style={{
                                fontWeight: 600,
                                fontSize: ".95rem",
                                cursor: "pointer",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                            onClick={() => setShowThreadList(!showThreadList)}
                        >
                            {showThreadList
                                ? "Conversations"
                                : activeThread?.title || "AI Agent"}
                        </p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: ".3rem" }}>
                        <div
                            style={{
                                cursor: "pointer",
                                padding: ".3rem",
                                borderRadius: ".3rem",
                                display: "flex",
                                alignItems: "center",
                            }}
                            onClick={handleNewThread}
                            title="New conversation"
                        >
                            <IoAdd size="1.2rem" />
                        </div>
                    </div>
                </div>

                {showThreadList ? (
                    <div
                        style={{ flex: 1, overflowY: "auto", padding: ".4rem" }}
                        className="scrollable-form"
                    >
                        {threads.map((thread) => (
                            <div
                                key={thread.id}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: ".6rem .8rem",
                                    borderRadius: ".4rem",
                                    cursor: "pointer",
                                    backgroundColor:
                                        thread.id === activeThreadId
                                            ? "var(--lightestGrey)"
                                            : "transparent",
                                }}
                                onClick={() => {
                                    setActiveThreadId(thread.id);
                                    setShowThreadList(false);
                                }}
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p
                                        style={{
                                            fontWeight:
                                                thread.id === activeThreadId ? 600 : 400,
                                            fontSize: ".9rem",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {thread.title}
                                    </p>
                                    <p
                                        style={{
                                            fontSize: ".75rem",
                                            color: "var(--grey)",
                                            paddingTop: ".15rem",
                                        }}
                                    >
                                        {thread.messages.length} messages ·{" "}
                                        {new Date(thread.createdAt).toLocaleDateString()}
                                    </p>
                                </div>
                                <div
                                    style={{
                                        padding: ".3rem",
                                        cursor: "pointer",
                                        color: "var(--grey)",
                                        flexShrink: 0,
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteThread(thread.id);
                                    }}
                                    title="Delete conversation"
                                >
                                    <IoTrashOutline size=".9rem" />
                                </div>
                            </div>
                        ))}
                        {threads.length === 0 && (
                            <p
                                style={{
                                    padding: "2rem 1rem",
                                    textAlign: "center",
                                    color: "var(--grey)",
                                    fontSize: ".9rem",
                                }}
                            >
                                No conversations yet
                            </p>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Messages */}
                        <div
                            ref={messagesContainerRef}
                            onScroll={handleMessagesScroll}
                            style={{ flex: 1, overflowY: "auto", padding: ".5rem" }}
                            className="scrollable-form"
                        >
                            {(!activeThread || activeThread.messages.length === 0) && (
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        height: "100%",
                                        gap: ".6rem",
                                        color: "var(--grey)",
                                        padding: "2rem 1.5rem",
                                        textAlign: "center",
                                    }}
                                >
                                    <div
                                        className="ai-msg-avatar"
                                        style={{
                                            height: "40px",
                                            minWidth: "40px",
                                            fontSize: "1rem",
                                        }}
                                    >
                                        AI
                                    </div>
                                    <p
                                        style={{
                                            fontSize: ".95rem",
                                            fontWeight: 600,
                                            color: "var(--darkGrey)",
                                        }}
                                    >
                                        AI Agent
                                    </p>
                                    <p style={{ fontSize: ".85rem", lineHeight: "1.5" }}>
                                        Ask me anything about members, plans, claims, or
                                        configurations.
                                    </p>
                                </div>
                            )}
                            {activeThread?.messages.map((msg) => (
                                <div
                                    key={msg.id}
                                    style={{
                                        display: "flex",
                                        justifyContent:
                                            msg.role === "user" ? "flex-end" : "flex-start",
                                        padding: ".3rem 0",
                                    }}
                                >
                                    <div
                                        style={{
                                            maxWidth: "95%",
                                            padding: ".6rem .8rem",
                                            borderRadius:
                                                msg.role === "user"
                                                    ? ".8rem .8rem 0 .8rem"
                                                    : ".8rem .8rem .8rem 0",
                                            backgroundColor:
                                                msg.role === "user"
                                                    ? "var(--lightBlue)"
                                                    : msg.isError
                                                        ? "var(--lightRed, #fff0f0)"
                                                        : "var(--lightestGrey)",
                                            color:
                                                msg.role === "user"
                                                    ? "var(--white)"
                                                    : "inherit",
                                            fontSize: ".9rem",
                                            lineHeight: "1.5",
                                        }}
                                    >
                                        {/* mode badge hidden */}
                                        {msg.role === "user" && msg.images?.length > 0 && (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: ".3rem",
                                                    flexWrap: "wrap",
                                                    marginBottom: msg.content ? ".4rem" : 0,
                                                }}
                                            >
                                                {msg.images.map((src, i) => (
                                                    <img
                                                        key={i}
                                                        src={src}
                                                        alt="attachment"
                                                        style={{
                                                            width: "80px",
                                                            height: "80px",
                                                            objectFit: "cover",
                                                            borderRadius: ".4rem",
                                                            border: "1px solid rgba(255,255,255,0.3)",
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        {msg.role === "assistant" ? (
                                            <>
                                                {msg.streaming && !msg.content ? (
                                                    <ThinkingDots />
                                                ) : msg.content ? (
                                                    <div className="ai-message">
                                                        <MarkdownRenderer>
                                                            {msg.content}
                                                        </MarkdownRenderer>
                                                    </div>
                                                ) : null}

                                                {msg.streaming && streamActivity && (
                                                    <ActivityIndicator
                                                        activity={streamActivity}
                                                    />
                                                )}

                                                {(msg.steps?.length > 0 || msg.todos?.length > 0) && (
                                                    <AgentStepsAccordion
                                                        steps={msg.steps || []}
                                                        todos={msg.todos}
                                                        isStreaming={!!msg.streaming}
                                                    />
                                                )}
                                            </>
                                        ) : (
                                            msg.content
                                        )}
                                    </div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Composer */}
                        <div style={{ padding: ".5rem .6rem" }}>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSend();
                                }}
                                style={{
                                    border: "1px solid var(--lighterGrey)",
                                    borderRadius: ".6rem",
                                    backgroundColor: "var(--lightestGrey)",
                                    overflow: "hidden",
                                }}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    style={{ display: "none" }}
                                    onChange={handleImageSelect}
                                />

                                {attachments.length > 0 && (
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: ".4rem",
                                            flexWrap: "wrap",
                                            padding: ".5rem .7rem 0",
                                        }}
                                    >
                                        {attachments.map((a) => (
                                            <div
                                                key={a.id}
                                                style={{
                                                    position: "relative",
                                                    width: "48px",
                                                    height: "48px",
                                                }}
                                            >
                                                <img
                                                    src={a.preview}
                                                    alt="upload preview"
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        borderRadius: ".3rem",
                                                        border: "1px solid var(--lighterGrey)",
                                                    }}
                                                />
                                                <div
                                                    onClick={() => removeAttachment(a.id)}
                                                    style={{
                                                        position: "absolute",
                                                        top: "-5px",
                                                        right: "-5px",
                                                        cursor: "pointer",
                                                        color: "var(--red)",
                                                        backgroundColor: "var(--white)",
                                                        borderRadius: "50%",
                                                        display: "flex",
                                                        lineHeight: 0,
                                                    }}
                                                >
                                                    <IoCloseCircle size=".85rem" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder={
                                        isStreaming
                                            ? "Waiting for response\u2026"
                                            : "Ask anything\u2026"
                                    }
                                    disabled={isStreaming}
                                    style={{
                                        width: "100%",
                                        padding: ".65rem .7rem .4rem",
                                        border: "none",
                                        outline: "none",
                                        fontSize: ".85rem",
                                        backgroundColor: "transparent",
                                        color: "var(--black)",
                                    }}
                                />

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: ".2rem .5rem .45rem",
                                    }}
                                >
                                    <div />

                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: ".1rem",
                                        }}
                                    >
                                        <div
                                            onClick={() => fileInputRef.current?.click()}
                                            title="Upload image"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: "1.7rem",
                                                height: "1.7rem",
                                                borderRadius: ".3rem",
                                                cursor: "pointer",
                                                color: "var(--grey)",
                                                transition: "background-color 150ms ease",
                                            }}
                                            onMouseEnter={(e) =>
                                            (e.currentTarget.style.backgroundColor =
                                                "var(--white)")
                                            }
                                            onMouseLeave={(e) =>
                                            (e.currentTarget.style.backgroundColor =
                                                "transparent")
                                            }
                                        >
                                            <IoImageOutline size="1rem" />
                                        </div>

                                        {isStreaming ? (
                                            <button
                                                type="button"
                                                onClick={handleStopStreaming}
                                                title="Stop generating"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "1.7rem",
                                                    height: "1.7rem",
                                                    borderRadius: "50%",
                                                    border: "none",
                                                    backgroundColor: "var(--red, #e74c3c)",
                                                    color: "var(--white)",
                                                    cursor: "pointer",
                                                    transition: "all 150ms ease",
                                                    padding: 0,
                                                }}
                                            >
                                                <IoStop size=".8rem" />
                                            </button>
                                        ) : (
                                            <button
                                                type="submit"
                                                disabled={
                                                    !inputValue.trim() &&
                                                    attachments.length === 0
                                                }
                                                title="Send message"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "1.7rem",
                                                    height: "1.7rem",
                                                    borderRadius: ".3rem",
                                                    border: "none",
                                                    backgroundColor:
                                                        inputValue.trim() ||
                                                            attachments.length > 0
                                                            ? "var(--skyDark)"
                                                            : "transparent",
                                                    color:
                                                        inputValue.trim() ||
                                                            attachments.length > 0
                                                            ? "var(--white)"
                                                            : "var(--grey)",
                                                    cursor:
                                                        inputValue.trim() ||
                                                            attachments.length > 0
                                                            ? "pointer"
                                                            : "default",
                                                    transition: "all 150ms ease",
                                                }}
                                            >
                                                <IoSend size=".8rem" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ChatSidebar;
